import { useEffect, useState } from "react";
import type { Photo, ProjectDetail } from "../../shared/types.ts";
import { api, photoUrl } from "../api.ts";
import { AreaBadge, Banner, Empty, Loading, formatDate } from "../components.tsx";
import { navigate } from "../useRoute.ts";

/** Finished projects, with before/after pairs when they exist. */
export function Archive() {
  const [projects, setProjects] = useState<ProjectDetail[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listProjects({ status: "done" })
      // The list endpoint doesn't carry photos, so fetch details for the gallery.
      .then((list) => Promise.all(list.map((project) => api.getProject(project.id))))
      .then(setProjects)
      .catch((err) => setError(err instanceof Error ? err.message : "Could not load the archive"));
  }, []);

  if (error) return <Banner kind="error">{error}</Banner>;
  if (!projects) return <Loading />;
  if (projects.length === 0) return <Empty>Nothing finished yet. Go do something!</Empty>;

  return (
    <div className="archive">
      <h2>Done ({projects.length})</h2>

      {projects.map((project) => {
        const before = project.photos.filter((p) => p.kind === "before");
        const after = project.photos.filter((p) => p.kind === "after");
        const others = project.photos.filter((p) => p.kind === "normal");
        const hasPair = before.length > 0 && after.length > 0;

        return (
          <section key={project.id} className="card">
            <div className="card-header">
              <button className="link strong" onClick={() => navigate(`/p/${project.id}`)}>
                {project.title}
              </button>
              <span className="muted small">{formatDate(project.completed_at)}</span>
            </div>
            <AreaBadge area={project.area} />

            {hasPair ? (
              <div className="before-after">
                <figure>
                  <Thumb photo={before[0]!} />
                  <figcaption>Before</figcaption>
                </figure>
                <figure>
                  <Thumb photo={after[0]!} />
                  <figcaption>After</figcaption>
                </figure>
              </div>
            ) : (
              [...before, ...after, ...others].length > 0 && (
                <div className="gallery">
                  {[...before, ...after, ...others].map((photo) => (
                    <Thumb key={photo.id} photo={photo} />
                  ))}
                </div>
              )
            )}
          </section>
        );
      })}
    </div>
  );
}

function Thumb({ photo }: { photo: Photo }) {
  return (
    <a href={photoUrl(photo.id, "display")} target="_blank" rel="noreferrer">
      <img src={photoUrl(photo.id, "thumb")} alt={photo.kind} loading="lazy" />
    </a>
  );
}
